import { lazy, Suspense, useEffect, useRef, useState } from "react";

// Storyset "Pana" scenes, recoloured by scripts/import-storyset.py. Their licence
// needs the footer credit. Layers carry data-motion; home.css animates them.
// Each scene is ~15 KB gzipped, so each is its own chunk, fetched near the viewport;
// the ratio reserves its space until then.
const scene = (load, ratio) => [
  lazy(() => load().then((module) => ({ default: module.ReactComponent }))),
  ratio,
];
const scenes = {
  team: scene(() => import("../illustrations/team.svg"), "3 / 2"),
  kidney: scene(() => import("../illustrations/kidney.svg"), "1"),
  doctor: scene(() => import("../illustrations/doctor.svg"), "1"),
  lab: scene(() => import("../illustrations/lab.svg"), "3 / 2"),
  heart: scene(() => import("../illustrations/heart.svg"), "1"),
  report: scene(() => import("../illustrations/report.svg"), "1"),
  membership: scene(() => import("../illustrations/membership.svg"), "1"),
  contact: scene(() => import("../illustrations/contact.svg"), "1"),
  privacy: scene(() => import("../illustrations/privacy.svg"), "1"),
  terms: scene(() => import("../illustrations/terms.svg"), "1"),
  refund: scene(() => import("../illustrations/refund.svg"), "3 / 2"),
  delete: scene(() => import("../illustrations/delete.svg"), "1"),
  consult: scene(() => import("../illustrations/consult.svg"), "3 / 2"),
  labwork: scene(() => import("../illustrations/labwork.svg"), "1"),
  records: scene(() => import("../illustrations/records.svg"), "1"),
};

export default function CareIllustration({ kind = "doctor", className = "" }) {
  const art = useRef(null);
  const [near, setNear] = useState(!window.IntersectionObserver);
  useEffect(() => {
    if (!window.IntersectionObserver) {
      art.current.classList.add("is-illustration-visible");
      return;
    }
    const load = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setNear(true),
      { rootMargin: "400px" },
    );
    const visible = new IntersectionObserver(
      ([entry]) =>
        art.current?.classList.toggle(
          "is-illustration-visible",
          entry.isIntersecting,
        ),
      { threshold: 0.05 },
    );
    load.observe(art.current);
    visible.observe(art.current);
    return () => {
      load.disconnect();
      visible.disconnect();
    };
  }, []);
  const [Scene, ratio] = scenes[kind];
  return (
    <span
      ref={art}
      className={`nc-care-art nc-scene-${kind} ${className}`.trim()}
      style={{ aspectRatio: ratio }}
      aria-hidden="true"
    >
      {near && (
        <Suspense fallback={null}>
          <Scene focusable="false" />
        </Suspense>
      )}
    </span>
  );
}
